// OIDC client: beginLogin, handleCallback, refresh, logout. Phase 8
// hard-cutover entry point. All bearer credentials flow through here.
//
// Wire model:
//   beginLogin       generates PKCE verifier + nonce + state, stashes
//                    in sessionStorage, redirects browser to
//                    /authorize on the AS.
//   handleCallback   reads ?code + ?state on /oidc/callback, pops the
//                    stashed verifier, POSTs form-encoded to /token
//                    with grant_type=authorization_code, verifies the
//                    id_token, persists tokens in the auth store.
//   refresh          POSTs form-encoded to /token with
//                    grant_type=refresh_token; persists the rotated
//                    pair.
//   logout           calls /end_session with id_token_hint, clears
//                    local state, redirects to login.
import { jwtVerify, createRemoteJWKSet, decodeJwt } from "jose";
import createClient from "openapi-fetch";
import type { paths } from "@content-buddy/genapi/openapi_app";

import { clearAuthTokens, readAuthTokens, writeAuthTokens, type AuthTokens } from "@content-buddy/shared";
import {
  codeChallengeFromVerifier,
  generateCodeVerifier,
  randomNonce,
  randomState,
} from "@content-buddy/shared/hooks/oidc/pkce";
import { getDiscovery } from "@content-buddy/shared/hooks/oidc/discovery";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api/v1";

const CLIENT_ID = (import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined) ?? "contentbuddy-spa-web";
// offline_access is REQUIRED for the silent-refresh flow: the AS issues a
// refresh token only when the granted scope carries it (OIDC Core 11), so
// dropping it here logs the user out at the first access-token expiry.
const SCOPE = "openid access offline_access";
const CALLBACK_PATH = "/auth/oidc/callback";

const PKCE_KEY = "oidc-pkce";

// loggingOut guards against a redirect race: clearAuthTokens() inside
// logout() synchronously flips the shared auth store, which re-renders any
// mounted ProtectedRoute with isAuthenticated=false BEFORE the browser has
// actually navigated to /end_session (window.location.assign schedules the
// navigation; it does not preempt still-running script/effects). Without
// this guard, ProtectedRoute's own beginLogin() can fire in that window and
// its async /authorize redirect can win the race, riding the session cookie
// that /end_session hasn't revoked yet — silently logging the user back in
// right after they clicked "sign out". isLoggingOut() lets ProtectedRoute
// skip its own redirect while one is already in flight from logout().
let loggingOut = false;

export function isLoggingOut(): boolean {
  return loggingOut;
}

interface PendingPKCE {
  verifier: string;
  nonce: string;
  state: string;
  returnTo: string;
}

function stashPKCE(p: PendingPKCE): void {
  sessionStorage.setItem(PKCE_KEY, JSON.stringify(p));
}

function popPKCE(): PendingPKCE | null {
  const raw = sessionStorage.getItem(PKCE_KEY);
  sessionStorage.removeItem(PKCE_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as PendingPKCE;
  } catch {
    return null;
  }
}

export class OIDCFlowError extends Error {
  code: string;
  // status is the HTTP status the failing call returned, when there is one
  // (a network error carries none). exchangeOn401 (rpc/client.ts, D5) reads
  // this to distinguish "exchange itself 401'd" from "exchange itself
  // 403'd" - a finer distinction than the two-bucket `code` mapping D7 uses
  // for user-facing copy.
  status?: number;
  // retryable tells CallbackPage whether to offer "Try again". Defaults to
  // true because every OIDC failure other than the exchange's own reason
  // table is transient (a network fault, a malformed callback, a stale
  // PKCE transaction) and already showed the retry affordance; a terminal
  // failure must pass `false` explicitly.
  retryable: boolean;
  // diagnostic carries the server's own error_description when `message` has
  // been replaced with human-facing copy (see postTokenForm's csrf_failed
  // mapping) -- console.error'd for support/devtools, never rendered, so the
  // exact server-side reason is not lost just because the user shouldn't
  // read it verbatim.
  diagnostic?: string;
  constructor(code: string, message: string, status?: number, retryable = true, diagnostic?: string) {
    super(message);
    this.code = code;
    this.status = status;
    this.retryable = retryable;
    this.diagnostic = diagnostic;
  }
}

function redirectURI(): string {
  return window.location.origin + CALLBACK_PATH;
}

// beginLogin redirects the browser into the AS authorize flow.
// `returnTo` is preserved so handleCallback can land the user back on
// the page they originally tried to reach.
export async function beginLogin(returnTo?: string): Promise<void> {
  const disc = await getDiscovery();
  const verifier = generateCodeVerifier();
  const challenge = await codeChallengeFromVerifier(verifier);
  const state = randomState();
  const nonce = randomNonce();
  stashPKCE({ verifier, nonce, state, returnTo: returnTo ?? window.location.pathname });

  const params = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: redirectURI(),
    scope: SCOPE,
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.assign(`${disc.authorization_endpoint}?${params.toString()}`);
}

// silentReauth attempts a no-UI session extension via
// /authorize?prompt=none. On success the AS redirects to /oidc/callback
// with a fresh code; on failure (login_required) it redirects with an
// error param, which handleCallback surfaces.
export async function silentReauth(): Promise<void> {
  const disc = await getDiscovery();
  const verifier = generateCodeVerifier();
  const challenge = await codeChallengeFromVerifier(verifier);
  const state = randomState();
  const nonce = randomNonce();
  stashPKCE({ verifier, nonce, state, returnTo: window.location.pathname });

  const params = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: redirectURI(),
    scope: SCOPE,
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: "S256",
    prompt: "none",
  });
  window.location.assign(`${disc.authorization_endpoint}?${params.toString()}`);
}

interface TokenResponseShape {
  access_token: string;
  token_type: string;
  expires_in: number;
  id_token?: string;
  scope?: string;
  error?: string;
  error_description?: string;
}

// tokenOriginRefusedMessage is what a user sees when the AS's
// browser-origin guard (refresh_origin_guard.go) refuses a /token request
// (403 csrf_failed). The server's own error_description ("<endpoint>
// request origin not allowed", R13) is an accurate diagnostic, not user
// copy — it names an internal control the user cannot act on, and it does
// NOT name the rejected origin (the wire body carries no origin field), so
// there is no server-supplied fact to surface. `origin` is read from
// window.location.origin instead: the user cannot see it in the URL bar in
// a form they can reliably transcribe into a support ticket (scheme, host,
// port, no path), and that address is the one fact that resolves the
// ticket. "isn't allowed" rather than "doesn't recognize": this is an
// allowlist decision, and the origin may be well known and deliberately
// blocked. Kept off `OIDCFlowError.message` as `error_description` --
// instead carried as `.diagnostic` -- so the server's own text is not lost,
// only kept off-screen.
export function tokenOriginRefusedMessage(origin: string): string {
  return `We couldn't sign you in — this site isn't allowed to sign in from ${origin}. Contact your administrator and include that address.`;
}

// postTokenForm POSTs to /token with credentials included both ways: the
// browser must SEND the __Host-as-refresh cookie on a refresh-grant request
// (there is no other way to present the refresh token — see authStore.ts)
// and must accept the freshly-minted __Host-as-refresh Set-Cookie the AS
// returns on every grant. The refresh token itself never appears in
// TokenResponseShape; it never touches JS (R1 finding).
async function postTokenForm(body: URLSearchParams): Promise<TokenResponseShape> {
  const disc = await getDiscovery();
  const resp = await fetch(disc.token_endpoint, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  const parsed = (await resp.json().catch(() => ({}))) as TokenResponseShape;
  if (!resp.ok) {
    if (resp.status === 403 && parsed.error === "csrf_failed") {
      // eslint-disable-next-line no-console -- diagnostic only, never rendered (see OIDCFlowError.diagnostic)
      console.error("oidc: /token request origin refused", parsed.error_description);
      // retryable: false is load-bearing -- the guard's answer is
      // deterministic for this browser (the allowlist, not the request, is
      // wrong), so a "Try again" affordance would only repeat the refusal.
      throw new OIDCFlowError(
        "csrf_failed",
        tokenOriginRefusedMessage(window.location.origin),
        resp.status,
        false,
        parsed.error_description,
      );
    }
    throw new OIDCFlowError(
      parsed.error ?? "request_failed",
      parsed.error_description ?? `${resp.status}`,
      resp.status,
    );
  }
  return parsed;
}

async function verifyAndPersist(resp: TokenResponseShape, expectedNonce: string | null): Promise<AuthTokens> {
  if (!resp.id_token) {
    throw new OIDCFlowError("id_token_missing", "AS response omitted id_token");
  }
  const disc = await getDiscovery();
  const jwks = createRemoteJWKSet(new URL(disc.jwks_uri));
  const { payload } = await jwtVerify(resp.id_token, jwks, {
    issuer: disc.issuer,
    audience: CLIENT_ID,
    algorithms: disc.id_token_signing_alg_values_supported,
  });
  if (expectedNonce && payload.nonce !== expectedNonce) {
    throw new OIDCFlowError("nonce_mismatch", "id_token nonce does not match the authorize request");
  }
  const tokens: AuthTokens = {
    accessToken: resp.access_token,
    idToken: resp.id_token,
    accessTokenExpiresAt: Math.floor(Date.now() / 1000) + resp.expires_in,
    sub: typeof payload.sub === "string" ? payload.sub : null,
    // The app token exchange (SCENARIO-08) runs separately, after this AS
    // token is persisted — see handleCallback. Not set here.
    appToken: null,
    appTokenExpiresAt: null,
  };
  writeAuthTokens(tokens);
  return tokens;
}

// exchangeForAppToken calls edge-app's own POST /me/session (SCENARIO-08)
// with the AS access token supplied by the caller as the bearer, and
// returns the resulting application token + its expiry. Uses openapi-fetch
// directly (not useFetchClientAuth, which reads the token from React state
// — this is called both outside any component (handleCallback, right after
// the AS token is minted) and from rpc/client.ts's exchangeOn401 ladder
// (D5), which must NOT go through useFetchClientAuth's own client or the
// retry ladder would recurse into itself).
//
// Exported (not just used internally) for exchangeOn401 (D5) — this module
// does not import rpc/client.ts, so there is no import cycle.
//
// Retryability depends on status, not on reason alone (R6): a 403 is always
// non-retryable, whatever its reason; a non-403 exchange failure
// ("session_exchange_failed") is always retryable. The reason lookup below
// runs only inside the 403 arm — that nesting, not the table's shape, is
// what keeps the two cases from collapsing into one. Every OIDCFlowError
// here also carries the response's HTTP status (when there is one) so
// exchangeOn401's ladder can distinguish "exchange itself 401'd" from
// "exchange itself 403'd" without its own second parse of the response.
function reasonFrom(body: unknown): string | undefined {
  if (typeof body === "object" && body !== null && "reason" in body && typeof body.reason === "string") {
    return body.reason;
  }
  return undefined;
}

interface ForbiddenEntry {
  code: string;
  message: string;
  retryable: boolean;
}

// REFUSAL_BY_REASON maps the exchange's 403 wire reason to the internal
// code, copy and retryability CallbackPage renders. Both entries are
// non-retryable: a 403 is terminal whatever its reason (R6), and the
// non-403 arm below never consults this table. A Map, not a Record: an
// attacker-controlled reason string looked up against a plain object risks
// resolving to a prototype property (e.g. "constructor") instead of
// throwing, silently building a broken error. Not exported: callers assert
// through exchangeForAppToken / handleCallback, never against the table
// directly.
const REFUSAL_BY_REASON = new Map<string, ForbiddenEntry>([
  [
    "account_membership_not_active",
    {
      code: "account_membership_not_active",
      message: "Your access to this account has been suspended. Contact an administrator.",
      retryable: false,
    },
  ],
  [
    "account_membership_none",
    {
      code: "account_membership_none",
      message: "You are not a member of any account. Ask an account admin to invite you.",
      retryable: false,
    },
  ],
]);

// FORBIDDEN_FALLBACK is the entry an absent or unrecognized 403 reason
// takes — an older backend, a reason this SPA build predates, or a retired
// spelling. It names no cause, because none is known: asserting the
// suspended copy (or any other table entry's) here would claim a specific
// refusal the server never actually sent. R6 requires only that such a 403
// stay non-retryable.
const FORBIDDEN_FALLBACK: ForbiddenEntry = {
  code: "session_exchange_forbidden",
  message: "You do not have access to this account. Contact an account administrator.",
  retryable: false,
};

export async function exchangeForAppToken(
  asAccessToken: string,
): Promise<{ appToken: string; appTokenExpiresAt: number }> {
  const client = createClient<paths>({ baseUrl: API_URL });
  const { data, error, response } = await client.POST("/me/session", {
    headers: { Authorization: `Bearer ${asAccessToken}` },
    body: {},
  });
  if (!data) {
    if (response.status === 403) {
      const reason = reasonFrom(error);
      const entry = (reason && REFUSAL_BY_REASON.get(reason)) || FORBIDDEN_FALLBACK;
      throw new OIDCFlowError(entry.code, entry.message, response.status, entry.retryable);
    }
    throw new OIDCFlowError(
      "session_exchange_failed",
      "We couldn't start your session. Please try again.",
      response.status,
    );
  }
  return {
    appToken: data.accessToken,
    appTokenExpiresAt: Math.floor(Date.now() / 1000) + data.expiresIn,
  };
}

// handleCallback finishes the authorize-code leg. Returns the SPA
// route the caller should navigate to after successful exchange (the
// returnTo captured by beginLogin).
//
// This function is single flight and in React strict mode cannot be called twice.
export async function handleCallback(search: URLSearchParams): Promise<{ returnTo: string }> {
  const err = search.get("error");
  if (err) {
    throw new OIDCFlowError(err, search.get("error_description") ?? "AS returned error");
  }
  const code = search.get("code");
  const stateParam = search.get("state");
  if (!code || !stateParam) {
    throw new OIDCFlowError("invalid_response", "callback missing code or state");
  }

  const pkce = popPKCE();
  if (!pkce) {
    throw new OIDCFlowError("no_pkce", "no pending PKCE transaction found");
  }
  if (pkce.state !== stateParam) {
    throw new OIDCFlowError("state_mismatch", "state parameter does not match");
  }
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    client_id: CLIENT_ID,
    redirect_uri: redirectURI(),
    code_verifier: pkce.verifier,
  });
  const resp = await postTokenForm(body);
  const tokens = await verifyAndPersist(resp, pkce.nonce);

  // SCENARIO-08: exchange the freshly-minted AS token for an application
  // token right after the callback, and store both. Deliberately NOT
  // wrapped in try/catch — a failed exchange should surface the same way
  // any other callback failure does (OIDCCallbackPage shows the error);
  // silently swallowing it would leave the caller believing sign-in
  // succeeded with no app token to make any /api/v1/* call.
  const appTokens = await exchangeForAppToken(resp.access_token);
  writeAuthTokens({ ...tokens, ...appTokens });

  return { returnTo: pkce.returnTo };
}

// refresh exchanges the refresh token for a fresh access + id_token pair.
// The refresh token itself is never read here — it lives only in the
// __Host-as-refresh HttpOnly cookie, which postTokenForm's
// credentials:"include" attaches automatically. Throws
// OIDCFlowError("invalid_grant") when the token has been revoked or the
// family burned, or ("invalid_request") when there is no cookie at all;
// callers respond by tearing down state and re-running beginLogin.
export async function refresh(): Promise<AuthTokens> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: CLIENT_ID,
  });
  const resp = await postTokenForm(body);
  // Refresh-grant id_tokens do not carry the original nonce; OIDC
  // Core §12.2 says nonce SHOULD match the original. We trust the AS
  // (which only re-signs claims it minted) and skip the check here.
  return verifyAndPersist(resp, null);
}

// signedOutPath is the app's OWN post-logout landing page. It lives outside
// ProtectedRoute, so it renders a "signed out" screen with an explicit
// sign-in action instead of auto-firing beginLogin. We register it as the
// post_logout_redirect_uri so the AS bounces the browser back here (NOT to
// "/", which the auth guard would forward to a fresh login). It must be in
// the client's post_logout_redirect_uris allowlist.
const SIGNED_OUT_PATH = "/auth/signed-out";

// logout drives RP-Initiated Logout: the AS clears the session cookie +
// revokes refresh tokens, then bounces to our signed-out landing page.
//
// ORDER IS LOAD-BEARING. We resolve the full redirect target BEFORE
// clearing local tokens. Clearing first and then awaiting getDiscovery
// opens a window where a render observes empty tokens and ProtectedRoute
// fires beginLogin(), re-entering /authorize with the still-live session
// cookie and silently logging the user back in (the intermittent
// "logged back in with credentials" race). Capturing idToken up front
// also makes a double-invoke (React StrictMode, rapid clicks) idempotent.
export async function logout(): Promise<void> {
  loggingOut = true;
  const { idToken } = readAuthTokens();
  const signedOut = window.location.origin + SIGNED_OUT_PATH;

  let target = signedOut;
  try {
    const disc = await getDiscovery();
    if (idToken) {
      const params = new URLSearchParams({
        id_token_hint: idToken,
        client_id: CLIENT_ID,
        post_logout_redirect_uri: signedOut,
      });
      target = `${disc.end_session_endpoint}?${params.toString()}`;
    }
    // No id_token means no RP-initiated logout is possible (the AS requires
    // the hint); fall through to the local signed-out page.
  } catch {
    target = signedOut;
  }

  clearAuthTokens();
  window.location.assign(target);
}

// expiryWithinSeconds reports whether the cached access token is
// within `seconds` of expiry — a proactive-refresh hint.
//
// KNOWN DEBT (SCENARIO-06): this is exported but not yet wired to a
// periodic check or React hook, so proactive refresh is not active. The
// reactive path (refreshOn401 in rpc/client.ts retries a single 401 after
// rotating the token) is the only refresh trigger today and satisfies the
// S-06 acceptance text. Wiring an early refresh off this helper is deferred
// to a token-lifecycle scenario.
export function expiryWithinSeconds(seconds: number): boolean {
  const { accessTokenExpiresAt } = readAuthTokens();
  if (!accessTokenExpiresAt) return false;
  return accessTokenExpiresAt - Math.floor(Date.now() / 1000) < seconds;
}

// UserInfo is the identity claims payload returned by the AS /userinfo
// endpoint (OIDC Core 1.0 §5.3). email_verified is the AUTHORITATIVE live
// signal (AS: status == ACTIVE) — fresher than decoding the cached id_token.
export interface UserInfo {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
}

// fetchUserInfo GETs the AS /userinfo endpoint with the cached access token
// as a Bearer credential and returns the live identity claims. It is
// authenticated by the Bearer token ONLY (no cookie / credentials mode). It
// fails closed: a missing access token, a non-2xx response, or a network
// error all throw OIDCFlowError so a caller can never mistake a failure for a
// verified identity.
export async function fetchUserInfo(): Promise<UserInfo> {
  const { accessToken } = readAuthTokens();
  if (!accessToken) {
    throw new OIDCFlowError("no_access_token", "no access token available for /userinfo");
  }
  const disc = await getDiscovery();
  const endpoint = disc.userinfo_endpoint ?? disc.issuer + "/userinfo";
  const resp = await fetch(endpoint, {
    // Bearer-only: never send cookies, even cross-origin. Documents the
    // auth model and stops a future refactor from leaking a session cookie.
    credentials: "omit",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!resp.ok) {
    throw new OIDCFlowError("userinfo_failed", `/userinfo returned ${resp.status}`);
  }
  return (await resp.json()) as UserInfo;
}

// idTokenSub returns the cached sub claim without re-verifying the
// id_token. The token was verified at persistence time; trusting the
// cached claim is fine for UI decisions.
export function idTokenSub(): string | null {
  const { idToken } = readAuthTokens();
  if (!idToken) return null;
  try {
    const payload = decodeJwt(idToken);
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

// idTokenEmailVerified reads the cached id_token's email_verified claim
// (stamped live by the AS at grant time, email_verified = status==ACTIVE).
// The token was verified at persistence time, so decoding the cached claim
// is fine for UI decisions. Defaults to false (fail-safe): a missing/absent
// or malformed claim must never grant entrance to an unverified account.
export function idTokenEmailVerified(): boolean {
  const { idToken } = readAuthTokens();
  if (!idToken) return false;
  try {
    const payload = decodeJwt(idToken);
    return payload.email_verified === true;
  } catch {
    return false;
  }
}
